import Phaser from 'phaser';
import { PLAYER_SPEED, PLAYER_DISPLAY_SIZE } from '../constants';
import { cartToIso, isoDepth } from '../utils/IsoUtils';

/**
 * PlayerController
 * ────────────────
 * Handles keyboard AND touch input and moves the player sprite in the
 * isometric world.  Touch: tap and drag in the direction you want to move;
 * release to stop.
 */
export class PlayerController {
  /**
   * @param {Phaser.Scene} scene
   * @param {number} startCartX
   * @param {number} startCartY
   */
  constructor(scene, startCartX = 0, startCartY = 0) {
    this.scene = scene;

    // Position in cartesian (grid) space – can be fractional
    this.cartX = startCartX;
    this.cartY = startCartY;

    // Create animated sprite at the projected position
    const { x, y } = cartToIso(this.cartX, this.cartY);
    this.sprite = scene.add.sprite(x, y, 'peasant', 0);
    this.sprite.setDisplaySize(PLAYER_DISPLAY_SIZE, PLAYER_DISPLAY_SIZE);
    this.sprite.setOrigin(0.5, 0.8); // anchor near feet
    this.sprite.setDepth(isoDepth(startCartX, startCartY));

    this.currentAnim = 'idle';
    this.sprite.play('idle');

    // Keyboard
    this.cursors = scene.input.keyboard.createCursorKeys();
    this.wasd = scene.input.keyboard.addKeys({
      up: Phaser.Input.Keyboard.KeyCodes.W,
      down: Phaser.Input.Keyboard.KeyCodes.S,
      left: Phaser.Input.Keyboard.KeyCodes.A,
      right: Phaser.Input.Keyboard.KeyCodes.D,
    });

    // Touch state
    this.touchOrigin = null;  // { x, y } of where the finger first touched
    this.touchDx = 0;         // normalised screen-space direction (-1..1)
    this.touchDy = 0;
    this.TOUCH_DEADZONE = 12; // px before drag registers as movement

    this.setupTouch();
  }

  /* ── Touch input ──────────────────────────────────────────────── */

  setupTouch() {
    this.scene.input.on('pointerdown', (pointer) => {
      this.touchOrigin = { x: pointer.x, y: pointer.y };
      this.touchDx = 0;
      this.touchDy = 0;
    });

    this.scene.input.on('pointermove', (pointer) => {
      if (!this.touchOrigin || !pointer.isDown) return;

      const dx = pointer.x - this.touchOrigin.x;
      const dy = pointer.y - this.touchOrigin.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < this.TOUCH_DEADZONE) {
        this.touchDx = 0;
        this.touchDy = 0;
      } else {
        // Normalise to unit length
        this.touchDx = dx / dist;
        this.touchDy = dy / dist;
      }
    });

    this.scene.input.on('pointerup', () => {
      this.touchOrigin = null;
      this.touchDx = 0;
      this.touchDy = 0;
    });
  }

  /**
   * Pick the 8-direction compass key from raw screen-space input.
   * Accepts any float values, snaps to the nearest of 8 directions.
   */
  getDirection(dx, dy) {
    if (dx === 0 && dy === 0) return null;

    // Angle in radians, 0 = right, positive = clockwise (screen Y-down)
    const angle = Math.atan2(dy, dx);
    // Divide the circle into 8 sectors of 45° each
    // Sector 0 centred on "right" (east), going clockwise
    const sector = Math.round(angle / (Math.PI / 4));
    const dirs = ['e', 'se', 's', 'sw', 'w', 'nw', 'n', 'ne'];
    // sector ranges from -4 to 4; normalise to 0..7
    return dirs[((sector % 8) + 8) % 8];
  }

  /**
   * Call from scene.update(time, delta).
   * @param {number} delta – ms since last frame
   */
  update(delta) {
    const dt = delta / 1000; // seconds

    // Gather raw screen-space input from keyboard (-1, 0, or 1)
    let rawDx = 0;
    let rawDy = 0;
    if (this.cursors.up.isDown || this.wasd.up.isDown) rawDy -= 1;
    if (this.cursors.down.isDown || this.wasd.down.isDown) rawDy += 1;
    if (this.cursors.left.isDown || this.wasd.left.isDown) rawDx -= 1;
    if (this.cursors.right.isDown || this.wasd.right.isDown) rawDx += 1;

    // Merge touch input (touch takes priority if active)
    let screenDx = rawDx;
    let screenDy = rawDy;
    if (this.touchOrigin && (this.touchDx !== 0 || this.touchDy !== 0)) {
      screenDx = this.touchDx;
      screenDy = this.touchDy;
    }

    // Determine direction for animation
    const dir = this.getDirection(screenDx, screenDy);
    const isMoving = dir !== null;

    // Animation – all 8 directions have dedicated frames, no flipping needed
    if (isMoving) {
      const animKey = `walk_${dir}`;
      if (this.currentAnim !== animKey) {
        this.sprite.play(animKey);
        this.sprite.setFlipX(false);
        this.currentAnim = animKey;
      }
    } else if (this.currentAnim !== 'idle') {
      this.sprite.play('idle');
      this.sprite.setFlipX(false);
      this.currentAnim = 'idle';
    }

    // Normalise so movement speed is consistent regardless of direction
    const mag = Math.sqrt(screenDx * screenDx + screenDy * screenDy);
    if (mag > 0) {
      screenDx /= mag;
      screenDy /= mag;
    }

    // Rotate screen-space input into cartesian grid-space
    // Screen-up (-Y) = cart(-1,-1), Screen-right (+X) = cart(+1,-1)
    const cartDx = screenDx + screenDy;
    const cartDy = -screenDx + screenDy;

    // Foot offset: collision and depth use a point shifted up to the
    // archer's feet (sprite anchor is below the feet due to shadow).
    // 8 screen-px up = 1.0 in cart sum, split across both axes.
    const FOOT = 0.5;

    // Move in cartesian space, with collision against obstructions.
    // Try each axis independently so the player slides along blocked edges.
    const blocked = this.scene.chunkManager?.blocked;
    const stepX = cartDx * PLAYER_SPEED * dt;
    const stepY = cartDy * PLAYER_SPEED * dt;

    if (!blocked) {
      this.cartX += stepX;
      this.cartY += stepY;
    } else {
      const tryX = this.cartX + stepX;
      if (!this.isBlocked(tryX - FOOT, this.cartY - FOOT, blocked)) {
        this.cartX = tryX;
      }
      const tryY = this.cartY + stepY;
      if (!this.isBlocked(this.cartX - FOOT, tryY - FOOT, blocked)) {
        this.cartY = tryY;
      }
    }

    // Sprite position stays at cartX/cartY (unchanged rendering)
    const { x, y } = cartToIso(this.cartX, this.cartY);
    this.sprite.setPosition(Math.round(x), Math.round(y));

    // Depth stays at the sprite position (not foot-offset) for correct layering
    this.sprite.setDepth(isoDepth(this.cartX, this.cartY));
  }

  /**
   * Check if a position collides with any blocked tile.
   * Tests a small hitbox (±radius) around the position so the player
   * can't clip through tile corners.
   */
  isBlocked(cx, cy, blocked) {
    const r = 0.25; // collision radius in tile units
    const minTX = Math.floor(cx - r);
    const maxTX = Math.floor(cx + r);
    const minTY = Math.floor(cy - r);
    const maxTY = Math.floor(cy + r);

    for (let tx = minTX; tx <= maxTX; tx++) {
      for (let ty = minTY; ty <= maxTY; ty++) {
        if (blocked.has(`${tx},${ty}`)) return true;
      }
    }
    return false;
  }
}
